'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { AngleUpload, MainAngleReplace } from '@/features/angle';
import { pollAngleValidation } from '@/features/angle/lib/validation-polling';
import { useImageConstraints } from '@/shared/hooks';
import { RenderProgress, useAngleRendersData } from '@/features/design/render';
import {
  getRendersForConcept,
  filterCompletedRendersWithFilesByAngle,
} from '@/features/design/render/model/renders-cache';
import { useProjectAngles } from '@/shared/api/hooks/use-project-angles';
import { usePaywallPayload } from '@/features/subscription/model/subscription.selectors';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import {
  useActiveJobForConcept,
  useLatestRenderForConceptAngle,
  useCompletedRenderFromStore,
} from '@/features/design/job-queue';
import { ErrorBoundary } from '@/shared/ui';
import { ConceptAngleType, ClassTypes } from '@/shared/constants';
import { isAngleValidationFinished } from '@/shared/constants/status';
import { rendersService } from '@/shared/api/services/renders.service';
import { useConceptStore } from '@/features/design/concept/model/concept-editor-store';
import { onInstant } from '@/shared/api/websocket/realtime-sync.registry';
import {
  payloadObject,
  payloadString,
} from '@/shared/api/websocket/realtime-sync.payload';
import { useUser } from '@/features/auth/model';
import { useQueryClient } from '@tanstack/react-query';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { toast } from '@/shared/lib/toast';
import { ApiClientError } from '@/shared/lib/api';
import { useLimitErrorPaywall } from '@/shared/hooks';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import type { ConceptAngle, AngleStoreResponse, Angle } from '@/types/concept';
import type { Concept } from '@/types/concept';
import type { Project } from '@/types/project';
import type { Render } from '@/types/render';
import { MainAngleDisplay } from './MainAngleDisplay';
import { MainAngleUploadModal } from './MainAngleUploadModal';

interface MainViewProps {
  project: Project;
  concept: Concept;
  mainAngle: ConceptAngle | undefined;
  conceptAngles: ConceptAngle[];
  angles: Record<string, Angle>;
  limits: { angles?: number } | null;
  isMax: boolean;
  isArchived?: boolean;
  onAngleUploadSuccess: () => Promise<void>;
  onCreateNewConcept: (angleId: string) => Promise<void>;
  onEditNotes?: () => void;
  onRenameAngle?: () => void;
  onEditAngles?: () => void;
  onDeleteConcept?: () => void;
  onDraggingSliderChange?: (isDragging: boolean) => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  hideRenderButton?: boolean;
}

export function MainView({
  project,
  concept,
  mainAngle,
  conceptAngles,
  angles,
  limits,
  isMax,
  onEditNotes,
  onRenameAngle,
  onEditAngles,
  onDeleteConcept,
  onAngleUploadSuccess,
  onCreateNewConcept,
  onDraggingSliderChange,
  onSwipeLeft,
  onSwipeRight,
  hideRenderButton = false,
  isArchived = false,
}: MainViewProps) {
  const [showMainAngleUpload, setShowMainAngleUpload] = useState(false);
  const [showMainAngleReplace, setShowMainAngleReplace] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSavingAsReference, setIsSavingAsReference] = useState(false);
  const queryClient = useQueryClient();
  const router = useRouter();
  const tRender = useTranslations('render.options');
  const tConcept = useTranslations('concept');
  // Server-driven paywall for PROJECT_REFERENCE_LIMIT_EXCEEDED (422).
  // Shared dialog renders the server-owned copy and upgrade/manage actions.
  const routeLimitErrorToPaywall = useLimitErrorPaywall();

  // Project-library angle count — pre-flight source of truth so we never
  // send uploads that the server would reject with 422. Limit comes from
  // the server-driven paywall payload.
  const { data: projectAngles } = useProjectAngles(project.id);
  const projectAngleLibraryCount = projectAngles?.length ?? 0;
  const angleLibraryPayload = usePaywallPayload('ANGLE_LIBRARY_LIMIT_EXCEEDED');
  const conceptAnglePayload = usePaywallPayload('CONCEPT_ANGLE_LIMIT_EXCEEDED');
  const showPaywall = useLimitPaywallStore((s) => s.showPaywall);

  const showProjectAngleLibraryUpgrade = useCallback(() => {
    if (angleLibraryPayload) {
      showPaywall(angleLibraryPayload, 'ANGLE_LIBRARY_LIMIT_EXCEEDED');
    }
  }, [angleLibraryPayload, showPaywall]);
  const showConceptAngleUpgrade = useCallback(() => {
    if (conceptAnglePayload) {
      showPaywall(conceptAnglePayload, 'CONCEPT_ANGLE_LIMIT_EXCEEDED');
    }
  }, [conceptAnglePayload, showPaywall]);

  // Calculate max image height for viewport constraint
  const { maxHeightCss, maxHeightPx } = useImageConstraints();

  const { ui, toggleShowRender } = useConceptStore(
    useShallow((state) => ({
      ui: state.ui,
      toggleShowRender: state.toggleShowRender,
    }))
  );

  // Use unified hook for angle renders data
  const {
    completedRenders,
    currentRender,
    activeRenderJob,
    activeUpscaleJob,
    isRendering,
  } = useAngleRendersData({
    conceptId: concept.id,
    angleId: mainAngle?.id,
    selectedRenderId: mainAngle?.id
      ? ui.selectedRenderId[mainAngle.id]
      : undefined,
  });

  // For new concepts without mainAngle yet
  const activeRenderForConcept = useActiveJobForConcept(concept.id);
  const activeRender =
    activeRenderJob ||
    (!mainAngle && activeRenderForConcept?.angleType === 'main'
      ? activeRenderForConcept
      : undefined);

  // Track the latest render for detecting completion
  useLatestRenderForConceptAngle(mainAngle?.id);

  // Get completed render from Store immediately
  const completedRenderFromStore = useCompletedRenderFromStore(mainAngle?.id);

  // Track the angle being validated
  const validatingAngleIdRef = useRef<string | null>(null);
  // Abort handle for the polling fallback — the WS validation_completed
  // listener (and unmount/close) aborts it so only one path resolves.
  const validationAbortRef = useRef<AbortController | null>(null);

  // Realtime sync for WebSocket events
  const user = useUser();


  // Handle angle validation completed event. Payload carries the flat
  // `validation` meta — same shape as the ProjectAngleResource — so we branch
  // on `validation.status` instead of recombining is_valid/is_prohibited/etc.
  useEffect(() => {
    if (!user) return;

    const cleanup = onInstant('angles', async (event) => {
      if (event.action !== 'validation_completed') return;
      if (event.id !== validatingAngleIdRef.current) return;

      const validation = payloadObject(event.payload, 'validation');
      if (!validation) return;

      const status = payloadString(validation, 'status');
      if (!status || !isAngleValidationFinished(status)) return;

      // WS won the race — stop the polling fallback.
      validationAbortRef.current?.abort();
      validationAbortRef.current = null;

      const isValid = status === 'valid';

      if (!isValid) {
        const recommendations = payloadString(validation, 'recommendations');
        setValidationError(
          recommendations || tConcept('mainAngleUpload.notSuitable')
        );
        setIsValidating(false);
        validatingAngleIdRef.current = null;
      } else {
        const hasMainRender = completedRenders.length > 0;
        if (hasMainRender) {
          await onCreateNewConcept(validatingAngleIdRef.current!);
        } else {
          await onAngleUploadSuccess();
        }
        setShowMainAngleUpload(false);
        setIsValidating(false);
        setValidationError(null);
        validatingAngleIdRef.current = null;
      }
    });

    return cleanup;
  }, [
    user,
    completedRenders.length,
    onCreateNewConcept,
    onAngleUploadSuccess,
    tConcept,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      validationAbortRef.current?.abort();
    };
  }, []);

  const handleMainAngleUploadSuccess = useCallback(
    async (angleResponse: AngleStoreResponse | AngleStoreResponse[]) => {
      setIsValidating(true);
      setValidationError(null);

      const uploadedAngle = Array.isArray(angleResponse)
        ? angleResponse[0]
        : angleResponse;
      if (!uploadedAngle) {
        setIsValidating(false);
        return;
      }
      const angleId = uploadedAngle.id;
      validatingAngleIdRef.current = angleId;

      // Polling fallback with the platform-unified cadence (10s delay, 3s
      // interval, 600s processing budget). The WS listener above usually
      // resolves first and aborts this poll.
      validationAbortRef.current?.abort();
      const controller = new AbortController();
      validationAbortRef.current = controller;

      try {
        const validation = await pollAngleValidation(angleId, controller.signal);
        if (controller.signal.aborted) return;

        if (!validation) {
          setValidationError(tConcept('mainAngleUpload.validationTimeout'));
        } else if (validation.status !== 'valid') {
          setValidationError(
            validation.recommendations ||
              tConcept('mainAngleUpload.notSuitable')
          );
        } else {
          const completed = mainAngle
            ? filterCompletedRendersWithFilesByAngle(
                getRendersForConcept(concept.id),
                mainAngle.id
              )
            : [];
          const hasMainRender = completed.length > 0;
          if (hasMainRender) {
            await onCreateNewConcept(angleId);
          } else {
            await onAngleUploadSuccess();
          }
          setShowMainAngleUpload(false);
          setValidationError(null);
        }
      } catch (error) {
        // AbortError means the WS path (or unmount) already handled the result.
        if (controller.signal.aborted) return;
        console.error('Error checking validation:', error);
        setValidationError(
          tConcept('mainAngleUpload.validationCheckFailed')
        );
      }
      setIsValidating(false);
      validatingAngleIdRef.current = null;
      if (validationAbortRef.current === controller) {
        validationAbortRef.current = null;
      }
    },
    [mainAngle, concept.id, onCreateNewConcept, onAngleUploadSuccess, tConcept]
  );

  // Active render for display (even without mainAngle)
  const activeRenderForDisplay = activeRender
    ? ({
        type: ClassTypes.RENDER,
        id: activeRender.id,
        attributes: {
          status: activeRender.status,
          started_at: activeRender.startedAt,
          duration_predicted: activeRender.durationPredicted,
          priority: 0,
          settings: {},
          setup_hash: '',
          created_at: activeRender.startedAt ?? '',
          updated_at: activeRender.startedAt ?? '',
        },
      } as Render)
    : undefined;

  // No main angle - show upload or progress
  if (!mainAngle) {
    return (
      <>
        <div className="relative">
          {!limits || conceptAngles.length < (limits.angles || Infinity) ? (
            <ErrorBoundary>
              <AngleUpload
                conceptId={concept.id}
                onSuccess={onAngleUploadSuccess}
                onLimitReached={() => showConceptAngleUpgrade()}
                currentAngleCount={conceptAngles.length}
                currentProjectAngleLibraryCount={projectAngleLibraryCount}
                onProjectAngleLibraryLimitReached={() =>
                  showProjectAngleLibraryUpgrade()
                }
                angleType={ConceptAngleType.MAIN}
                uploadTitle={tConcept('mainUpload.title')}
                uploadDescription={tConcept('mainUpload.description')}
              />
            </ErrorBoundary>
          ) : (
            <div className="py-8 text-center">
              <p className="text-form-help text-muted-foreground mb-2">
                {tConcept('mainUpload.angleLimitReached', {
                  limit: limits.angles ?? 0,
                })}
              </p>
              {!isMax && (
                <button
                  onClick={() => showConceptAngleUpgrade()}
                  className="text-form-help text-primary hover:text-primary/80 underline"
                >
                  {tConcept('mainUpload.upgradeForMore')}
                </button>
              )}
            </div>
          )}
          {activeRenderForDisplay && (
            <RenderProgress
              render={activeRenderForDisplay}
              mode="inline"
              conceptId={concept.id}
              projectId={project.id}
              onComplete={() => {}}
              onError={() => {}}
            />
          )}
        </div>
      </>
    );
  }

  // Calculate render URL
  const renderFileUrl =
    (completedRenderFromStore?.jobType === 'render' ||
    completedRenderFromStore?.jobType === 'upscale'
      ? completedRenderFromStore.imageUrl
      : null) ||
    currentRender?.relationships?.file?.meta?.url ||
    currentRender?.attributes?.file_url;

  const shouldShowRender = !!(
    renderFileUrl && ui.showRenders[mainAngle.id] !== false
  );

  const handleSaveAsReference = async () => {
    if (!currentRender?.id) return;
    setIsSavingAsReference(true);
    try {
      await rendersService.saveRenderAsReference(currentRender.id);
      queryClient.invalidateQueries({
        queryKey: cacheKeys.references.list(project.id),
      });
      // Sonner supports `onClick` on the toast body at runtime (used here to
      // navigate to the references page when the user taps the toast), but the
      // type def for ExternalToast omits it — spread an untyped object so TS
      // doesn't fail excess-property check.
      const navigateToRefs = {
        onClick: () => router.push(`/project/${project.id}/references`),
      };
      toast.success(tRender('savedAsReference'), {
        style: { cursor: 'pointer' },
        ...navigateToRefs,
      });
    } catch (error) {
      if (routeLimitErrorToPaywall(error)) return;
      if (
        error instanceof ApiClientError &&
        typeof error.message === 'string' &&
        error.message.length > 0
      ) {
        toast.error(error.message);
      } else {
        toast.error(tRender('saveAsReferenceError'));
      }
    } finally {
      setIsSavingAsReference(false);
    }
  };

  const handleDeleteRender = async (renderId: string) => {
    try {
      await rendersService.deleteRender(renderId);
      // Invalidate renders cache
      queryClient.invalidateQueries({
        queryKey: cacheKeys.renders.all,
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.conceptAngles.all,
      });
    } catch (error) {
      console.error('Failed to delete render:', error);
    }
  };

  return (
    <>
      <MainAngleDisplay
        project={project}
        concept={concept}
        mainAngle={mainAngle}
        angles={angles}
        currentRender={currentRender}
        renderFileUrl={renderFileUrl}
        shouldShowRender={shouldShowRender}
        isRendering={isRendering}
        activeRenderJob={activeRenderJob}
        activeUpscaleJob={activeUpscaleJob}
        hideRenderButton={hideRenderButton}
        onToggleShowRender={() => toggleShowRender(mainAngle.id)}
        onDraggingSliderChange={onDraggingSliderChange}
        onSwipeLeft={onSwipeLeft}
        onSwipeRight={onSwipeRight}
        onStartNewPhoto={() => setShowMainAngleUpload(true)}
        onReplacePhoto={() => setShowMainAngleReplace(true)}
        onEditNotes={onEditNotes}
        onRenameAngle={onRenameAngle}
        onEditAngles={onEditAngles}
        onDeleteConcept={onDeleteConcept}
        onDeleteRender={handleDeleteRender}
        onSaveAsReference={handleSaveAsReference}
        isSavingAsReference={isSavingAsReference}
        // Pulse the positive-feedback icon for a render that just completed on
        // this screen (the transient completed job entry is the trigger; the
        // overlay latches it for its 4 s window).
        feedbackPulseSignal={
          completedRenderFromStore?.jobType === 'render'
            ? completedRenderFromStore.id
            : null
        }
        maxHeightCss={maxHeightCss}
        maxImageHeight={maxHeightPx ?? undefined}
        isArchived={isArchived}
      />

      {/* Main Angle Upload Modal */}
      {showMainAngleUpload && (
        <MainAngleUploadModal
          conceptId={concept.id}
          projectId={project.id}
          conceptAnglesCount={conceptAngles.length}
          hasCompletedRenders={completedRenders.length > 0}
          isValidating={isValidating}
          validationError={validationError}
          currentProjectAngleLibraryCount={projectAngleLibraryCount}
          onProjectAngleLibraryLimitReached={() => {
            setShowMainAngleUpload(false);
            showProjectAngleLibraryUpgrade();
          }}
          onClose={() => {
            validationAbortRef.current?.abort();
            validationAbortRef.current = null;
            setShowMainAngleUpload(false);
            setIsValidating(false);
            setValidationError(null);
          }}
          onSuccess={handleMainAngleUploadSuccess}
          onClearError={() => {
            setValidationError(null);
            setIsValidating(false);
          }}
          onLimitReached={() => {
            showConceptAngleUpgrade();
            setShowMainAngleUpload(false);
          }}
        />
      )}

      {/* Main Angle Replace Modal */}
      {showMainAngleReplace && mainAngle && (
        <MainAngleReplace
          projectId={project.id}
          conceptId={concept.id}
          conceptAngleId={mainAngle.id}
          conceptAngles={conceptAngles}
          hasRenders={completedRenders.length > 0}
          currentProjectAngleLibraryCount={projectAngleLibraryCount}
          onProjectAngleLibraryLimitReached={() => {
            setShowMainAngleReplace(false);
            showProjectAngleLibraryUpgrade();
          }}
          onSuccess={() => {
            setShowMainAngleReplace(false);
            onAngleUploadSuccess();
          }}
          onSwapSuccess={() => {
            setShowMainAngleReplace(false);
            onAngleUploadSuccess();
          }}
          onCancel={() => setShowMainAngleReplace(false)}
        />
      )}

      {/* ANGLE_LIBRARY / CONCEPT_ANGLE preflight dialogs are rendered by the
          root-mounted <LimitPaywallDialog /> using server-owned copy. */}
    </>
  );
}
